/**
 * KeyContactsTab Component
 *
 * Renders the Key Contacts tab content for CompanyDetails, including:
 * - Company details section (address, email, phone)
 * - Employee filters (desktop: MultiSelect dropdowns, mobile: popup)
 * - Filtered contacts list with ContactCard components
 *
 * SELF-CONTAINED MODE: When filterFields/onFilterFieldChange not provided,
 * this component manages filter state internally.
 *
 * CONTROLLED MODE: When filterFields and callbacks provided, parent manages state.
 */

import PropTypes from 'prop-types';
import { useState, useMemo, useEffect, useCallback } from 'react';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import MultiSelect from '../../../inputs/MultiSelect';
import MultiSelectRow from '../../../inputs/MultiSelectRow';
import FiltersChipsList from '../../../common/FiltersChipsList';
import ContactCard from '../../../contactCard';
import KeyContactsActionBar from './KeyContactsActionBar';
import DiscoverContactsPanel from './DiscoverContactsPanel';
import DiscoveredContactsSection from './DiscoveredContactsSection';
import {
  dedupeDiscoveredContacts,
  filterDiscoveredBySelectedLevels
} from './discoveredContactsUtils';
import { STREAM_B_ENABLED } from '../../../../utils/constants/featureFlags';
import useEnrichmentAccess from '../../../../hooks/useEnrichmentAccess';
import useSubscriptionStatus from '../../../../hooks/useSubscriptionStatus';
import useBulkReveal from '../../../../hooks/useBulkReveal';
import useEnrichmentJobPolling from '../../../../hooks/useEnrichmentJobPolling';
import { HEADER_MESSAGE_TYPES } from '../../../../utils/constants/header';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';
import { computeRevealCost } from './keyContactsCostUtils';
import * as Portal from '@radix-ui/react-portal';
import { useStore } from '../../../../store/store';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { FIELDS_TYPES } from '../../../../utils/constants/fields';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import { UI_OPTIONS_UTILS } from '../../../../utils/constants/uiOptions';
import {
  filterKeyContacts,
  safeHttpUrl,
  contactKey,
  partitionRevealTargets
} from './keyContactsUtils';
import { orderRoleBucketLabels } from '../../../../utils/constants/roleBuckets';

// Filter field definitions matching the structure expected by MultiSelect component
// Must include: ID (for DOM manipulation including Clear all button), PLACEHOLDER (hint text),
// TYPE (dropdown behavior), and VALUES_KEY (options from store) where applicable
const EMPLOYEES_FILTERS_FIELDS = {
  ROLE: {
    NAME: 'role',
    ID: 'ef_role',
    LABEL: 'By role',
    PLACEHOLDER: 'Role',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT
  },
  COUNTRY: {
    NAME: 'country',
    ID: 'ef_country',
    LABEL: 'By country of residence',
    PLACEHOLDER: 'Country of residence',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT
  },
  IS_SHAREHOLDER: {
    NAME: 'is_shareholder',
    ID: 'ef_shareholder',
    LABEL: 'Is Shareholder',
    PLACEHOLDER: 'Is Shareholder',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: UI_OPTION_KEYS.CONTACT_FILTER_IS_SHAREHOLDER
  },
  AGE: {
    NAME: 'age',
    ID: 'ef_age',
    LABEL: 'Age',
    PLACEHOLDER: 'Age',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: UI_OPTION_KEYS.CONTACT_FILTER_AGE
  }
};

const withoutValue = (values, value) => values.filter((v) => v !== value);

/**
 * TD-2505: build the active-filter chip descriptors for FiltersChipsList's
 * `chips` API. Module scope (rather than inline in the component) keeps the
 * arrow nesting within Sonar's 4-level limit — S2004.
 *
 * @param {Array} sources - [{ field, options, prefix }] in display order
 * @param {Object} filterFields - current filter values, keyed by field.NAME
 * @param {Function} onChange - (field, nextValues) filter setter
 * @returns {Array} chip descriptors, each owning its own onRemove
 */
const buildFilterChips = (sources, filterFields, onChange) =>
  sources.flatMap(({ field, options, prefix }) => {
    const selected = filterFields[field.NAME] || [];
    return (options || [])
      .filter(({ value }) => selected.includes(value))
      .map(({ name, value }) => ({
        name,
        value,
        prefix,
        onRemove: () => onChange(field, withoutValue(selected, value))
      }));
  });

/**
 * @param {Object} props
 * @param {Array} props.contacts - All contacts for the company
 * @param {Array} props.filteredContacts - Contacts filtered by current criteria
 * @param {string} props.companyName - Company name for LinkedIn search
 * @param {string|number} props.companyId - Company profile id (reveal context)
 * @param {string} props.companyAddress - Formatted company address
 * @param {string} props.companyGoogleMapsLink - Google Maps URL for address
 * @param {string} props.companyEmail - Company email address
 * @param {string} props.companyPhone - Company phone number
 * @param {string} props.layout - 'desktop' | 'mobile' | 'tablet'
 * @param {boolean} props.isPhoneOrTablet - Whether device is phone/tablet (for click-to-call)
 * @param {Object} props.filterFields - Current filter field values { role: [], country: [], ... }
 * @param {Array} props.roleFilterOptions - Options for role filter dropdown
 * @param {Array} props.countryFilterOptions - Options for country filter dropdown
 * @param {boolean} props.isMobileFilterMenuOpen - Whether mobile filter popup is open
 * @param {Function} props.onFilterFieldChange - Callback(fieldName, value) for filter changes
 * @param {Function} props.onToggleMobileFilterMenu - Callback to toggle mobile filter popup
 * @param {Function} props.onResetFilters - Callback to reset all filters
 */
// Default filter fields state structure
const DEFAULT_FILTER_FIELDS = {
  role: [],
  country: [],
  is_shareholder: [],
  age: []
};

// BE skip reasons (controllers/enrichment.js bulkReveal): the slug arrives as
// an opaque string. Surface a user-readable label instead.
const SKIP_REASON_LABELS = {
  not_found: 'contact not found',
  erasure: 'removed under data-protection request',
  // TD-2357: a discovered identity that failed to persist before enrichment.
  persist_failed: 'could not be saved'
};

/**
 * One company-detail row (address / email / phone): an icon plus a value that
 * is interactive only when a target exists. Extracted from KeyContactsTab to
 * shed cognitive complexity (S3776) and to render a native <button> instead of
 * a <div role="button"> when interactive (S6819) — natively focusable and
 * keyboard-operable, so the onKeyDown Enter/Space shim is no longer needed.
 * When not interactive (empty email/phone), a plain <div> is rendered so the
 * empty state never becomes focusable.
 */
export const CompanyDetailRow = ({ icon, isInteractive, onActivate, displayValue }) => (
  <div className="item">
    <div className="icon-wrap">
      <img src={icon} alt="" />
    </div>
    {isInteractive ? (
      <button type="button" className="text link" onClick={onActivate}>
        {displayValue}
      </button>
    ) : (
      <div className="text">{displayValue}</div>
    )}
  </div>
);

CompanyDetailRow.propTypes = {
  icon: PropTypes.string,
  isInteractive: PropTypes.bool,
  // Always supplied by callers; required so an interactive row that forgets it
  // surfaces a dev warning instead of a silently dead button.
  onActivate: PropTypes.func.isRequired,
  displayValue: PropTypes.string
};


// TD-2956: every bucket a contact belongs to. `role_buckets` is BE-stamped for stored contacts
// and FE-stamped by mapDiscoveredContact; the fallback covers a contact that reached the UI
// without either (a mid-session reveal through an older path), so it is never dropped from the
// option set. Module scope: pure, and it must not be rebuilt on every render inside a memo.
const bucketLabelsOf = (contact) =>
  contact.role_buckets ?? (contact.role_bucket ? [contact.role_bucket] : []);

const KeyContactsTab = ({
  contacts = [],
  filteredContacts: filteredContactsProp,
  companyName = '',
  companyId,
  companyAddress = '',
  companyGoogleMapsLink = '',
  companyEmail = '',
  companyPhone = '',
  layout = 'desktop',
  isPhoneOrTablet = false,
  filterFields: filterFieldsProp,
  roleFilterOptions = [],
  countryFilterOptions = [],
  isMobileFilterMenuOpen: isMobileFilterMenuOpenProp,
  onFilterFieldChange: onFilterFieldChangeProp,
  onToggleMobileFilterMenu: onToggleMobileFilterMenuProp,
  onResetFilters: onResetFiltersProp,
  onViewDetails,
  onOpenDetailsInNewTab,
  onBulkRevealComplete
}) => {
  // Self-contained mode: manage filter state internally when not provided via props
  const isSelfContained = filterFieldsProp === undefined;

  // Internal state for self-contained mode
  const [internalFilterFields, setInternalFilterFields] = useState(DEFAULT_FILTER_FIELDS);
  const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);
  // Phase 3b: selection state for bulk reveal. Owns selection independent of
  // filters so a contact filtered out then back in retains its selection.
  // Bulk-reveal action (Phase 3c) consumes this via the action bar's onReveal.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const enrichmentAccess = useEnrichmentAccess();

  // TD-2085 (Stream B): ephemeral discovered-contact results. Held raw here and
  // filtered/deduped reactively below so a revealed→persisted contact drops out
  // once the next keyContacts refetch includes it. Browser-only; cleared when
  // the company changes or the tab unmounts (AC13).
  const [discoveredRaw, setDiscoveredRaw] = useState([]);

  // TD-2497: the Discover panel's management-level selection, mirrored up so the
  // discovered list can be filtered by it. Deselecting a level hides its people;
  // re-selecting shows them again from `discoveredRaw` with no re-discovery (no
  // cost). Fed by the panel's onCriteriaChange (user toggles + post-search
  // auto-check). No companyId reset needed: it mirrors the panel, and the
  // discoveredRaw reset below already empties the section on company change.
  const [activeLevels, setActiveLevels] = useState([]);

  // Use controlled or self-contained values
  const filterFields = isSelfContained ? internalFilterFields : filterFieldsProp;
  const isMobileFilterMenuOpen = isSelfContained
    ? internalMobileMenuOpen
    : isMobileFilterMenuOpenProp;

  // Auto-close mobile filter menu when layout becomes desktop (responsive behavior)
  useEffect(() => {
    if (isSelfContained && layout === 'desktop' && internalMobileMenuOpen) {
      setInternalMobileMenuOpen(false);
    }
  }, [isSelfContained, layout, internalMobileMenuOpen]);

  // TD-2085: reset ephemeral discovery results when the company in view changes
  // so stale results from a previous company never leak onto another (the tab
  // can update in place without remounting).
  useEffect(() => {
    setDiscoveredRaw([]);
  }, [companyId]);

  // TD-2497: the Discover panel lives in a different subtree per layout
  // (KeyContactsActionBar early-returns the mobile sticky bar), so a
  // desktop/mobile flip across 1201px remounts it and clears its checklist.
  // `activeLevels` lives here and would survive, leaving an empty checklist
  // filtering a still-narrowed list that no tick could widen again. Clear the
  // display filter with it, so the two cannot disagree.
  useEffect(() => {
    setActiveLevels([]);
  }, [layout]);

  // Compute filtered contacts internally for self-contained mode
  const computedFilteredContacts = useMemo(() => {
    if (!isSelfContained) return filteredContactsProp || [];
    return filterKeyContacts(contacts, filterFields);
  }, [isSelfContained, filteredContactsProp, contacts, filterFields]);

  // Memoised so the derived memos below (selectableContacts, role options) get a
  // stable dependency instead of a fresh array each render (react-hooks churn).
  const filteredContacts = useMemo(
    () => (isSelfContained ? computedFilteredContacts : filteredContactsProp || []),
    [isSelfContained, computedFilteredContacts, filteredContactsProp]
  );

  // TD-2085: discovered list, deduped against the stored set (by name + LinkedIn
  // against ALL stored contacts, not just the filtered view). Reactive on
  // `contacts`, so a revealed→persisted discovered contact drops out once the
  // next keyContacts refetch surfaces it in the stored grid.
  const discoveredDeduped = useMemo(
    () => dedupeDiscoveredContacts(contacts, discoveredRaw),
    [contacts, discoveredRaw]
  );
  // TD-2497: first narrow the discovered list to the currently-selected management
  // levels (the Discover panel's checklist doubles as this section's filter), then
  // apply the top-of-tab Role/Country/Age filters on top so both sections stay in
  // sync. Deselecting a level drops its people here without touching discoveredRaw,
  // so re-selecting shows them again with no re-discovery.
  const discoveredLevelFiltered = useMemo(
    () => filterDiscoveredBySelectedLevels(discoveredDeduped, activeLevels),
    [discoveredDeduped, activeLevels]
  );
  const discoveredContacts = useMemo(
    () => filterKeyContacts(discoveredLevelFiltered, filterFields),
    [discoveredLevelFiltered, filterFields]
  );
  // TD-2085 Phase 6 (AC-019): discovered contacts carry identity, role and (since
  // TD-2358/TD-2273) a Surfe `country`, so the Country filter can match them —
  // but Age/Shareholder filters test CREFO-only fields and match none, and an
  // unmatched country still hides them. Surface how many results the top-of-tab
  // filters hid, so DiscoveredContactsSection can explain the empty state (with a
  // "Clear filters" affordance) instead of just vanishing. Counted against the
  // level-filtered set (TD-2497) so a level *deselection* — which "Clear filters"
  // does NOT undo — clears the section cleanly instead of showing that note.
  const discoveredHiddenByFilters = discoveredLevelFiltered.length - discoveredContacts.length;

  // TD-2422: role-filter options are the canonical buckets present across BOTH
  // sources — CREFO buckets from the BE-provided (bucketed) `roleFilterOptions`
  // plus discovered buckets stamped FE-side (mapDiscoveredContact). Derived from
  // the pre-filter discovered list so the option set stays stable as the user
  // filters, and ordered canonically.
  // TD-2956: also union each stored contact's OWN membership. A person can belong to two
  // buckets — `company_managers` collapses their roles to one and prefers the non-ownership
  // one, so a co-owning Geschäftsführer arrives as "Managing Director" with the shareholder
  // bucket only in `role_buckets`. The BE's `roleFilterOptions` is derived from the singular
  // bucket, so in a company where EVERY shareholder also manages it would offer no
  // shareholder option at all: the people are filterable, but nothing lists the bucket.
  //
  // Deriving it here as well as from the BE list is deliberate. It makes the option set a
  // function of the contacts actually on screen, so this works whatever the BE is serving —
  // the two repos deploy independently, and coupling the option list to a backend release
  // would leave a window where the filter is either missing or dead.
  const roleBucketOptions = useMemo(() => {
    const labels = new Set();
    (roleFilterOptions || []).forEach((option) => {
      if (option?.value) labels.add(option.value);
    });
    // Pre-filter lists on both sides, so the option set stays stable as the user filters.
    (contacts || []).forEach((contact) => bucketLabelsOf(contact).forEach((b) => labels.add(b)));
    discoveredDeduped.forEach((contact) => bucketLabelsOf(contact).forEach((b) => labels.add(b)));
    return orderRoleBucketLabels(labels).map((value) => ({ name: value, value }));
  }, [roleFilterOptions, contacts, discoveredDeduped]);

  // TD-2421: the selectable set spans stored (filtered) + discovered (filtered)
  // contacts, so "Select all", the action-bar count and the cost preview all
  // account for a mixed selection.
  const selectableContacts = useMemo(
    () => [...filteredContacts, ...discoveredContacts],
    [filteredContacts, discoveredContacts]
  );

  // TD-2549: the action bar's cost preview must count every selected contact a
  // reveal would bill — including ones the active top-of-tab filter hides.
  // `selectableContacts` holds only the visible rows, so also hand the bar the
  // full billable universe: the SAME lists handleBulkReveal resolves the
  // selection from — the full stored `contacts` plus the level-filtered
  // discovered list (both wider than their filtered, visible counterparts).
  const allSelectableContacts = useMemo(
    () => [...contacts, ...discoveredLevelFiltered],
    [contacts, discoveredLevelFiltered]
  );

  // Handler functions - use provided callbacks or internal handlers
  const handleFilterFieldChange = (field, value) => {
    if (onFilterFieldChangeProp) {
      onFilterFieldChangeProp(field, value);
    } else {
      setInternalFilterFields((prev) => ({
        ...prev,
        [field.NAME]: value
      }));
    }
  };

  const handleToggleMobileFilterMenu = () => {
    if (onToggleMobileFilterMenuProp) {
      onToggleMobileFilterMenuProp();
    } else {
      setInternalMobileMenuOpen((prev) => !prev);
    }
  };

  const handleResetFilters = () => {
    if (onResetFiltersProp) {
      onResetFiltersProp();
    } else {
      setInternalFilterFields(DEFAULT_FILTER_FIELDS);
    }
  };

  const hasContacts = !!contacts?.length;
  const hasFilteredContacts = !!filteredContacts?.length;

  // Phase 3b selection handlers. "Select all" operates on the *filtered*
  // contact set (what the user actually sees), matching the visible-only
  // semantics of master checkboxes in Gmail.
  const handleSelectionChange = useCallback((contactId, nextSelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (nextSelected) next.add(contactId);
      else next.delete(contactId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    // TD-2421: select all visible contacts across both lists, keyed uniformly.
    setSelectedIds(new Set(selectableContacts.map(contactKey)));
  }, [selectableContacts]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Phase 3c wiring: bulk reveal + cost gating + overage modal + job polling.
  const store = useStore();
  // Ungated on purpose. The `enabled: enrichmentAccess.enabled` gate existed to
  // keep this from being a second fetch, but `enabled` only turns true once the
  // first response has landed, so it fetched sequentially with nothing in
  // flight to share. Reading the shared snapshot, it costs no request at all.
  const subscription = useSubscriptionStatus();
  const {
    jobId,
    skipped: bulkSkipped,
    isSubmitting,
    triggerBulkReveal,
    resetBulkReveal
  } = useBulkReveal({ refetchAccess: enrichmentAccess.refetch });
  const { status: jobStatus, job: jobResult } = useEnrichmentJobPolling(jobId);

  // TD-2427: the (contact keys + field[s]) submitted to the in-flight bulk job,
  // captured at trigger time so each selected card can show the same spinner a
  // single reveal shows. The job-status poll returns only aggregate counts and
  // `selectedIds` is cleared on completion, so neither can be re-derived at
  // render — this is the authoritative in-flight set. `null` = no job running.
  // Shape: { keys: Set<contactKey>, fields: ['email'] | ['phone'] | ['email','phone'] }.
  const [bulkRevealing, setBulkRevealing] = useState(null);

  // A trigger that never became a job (useBulkReveal.onError: isSubmitting
  // dropped, no jobId → polling stays 'idle', so the terminal-status clear
  // below can't fire) must also clear the overlay, or the submitted cards spin
  // forever — and, since the spinner replaces the reveal button, block single
  // reveals too. Never fires on a healthy trigger: the overlay set and
  // setIsSubmitting(true) land in the same handler batch, and on success jobId
  // and isSubmitting(false) land in the same onSuccess batch.
  useEffect(() => {
    if (bulkRevealing && !isSubmitting && !jobId) setBulkRevealing(null);
  }, [bulkRevealing, isSubmitting, jobId]);

  // Field → fields[] mapping and the matching cost key. Centralised so a future
  // field (e.g., 'work_phone') only needs an entry here, not three branches.
  const FIELD_CONFIG = useMemo(
    () => ({
      email: { fields: ['email'], costKey: 'email' },
      phone: { fields: ['phone'], costKey: 'phone' },
      both: { fields: ['email', 'phone'], costKey: 'both' }
    }),
    []
  );

  const handleBulkReveal = useCallback(
    async (field, skipPreviouslyEnriched) => {
      const config = FIELD_CONFIG[field];
      if (!config) {
        // Unknown field — refuse to fall through to the most-expensive option.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(`[KeyContactsTab] handleBulkReveal: unknown field "${field}"`);
        }
        return;
      }
      const { fields, costKey } = config;
      // TD-2421: a selection can span stored CREFO contacts AND ephemeral
      // discovered contacts. Resolve both from their full (pre-filter) lists so
      // a selection held while filters change is preserved, keyed uniformly.
      // TD-2497: the discovered side resolves from the LEVEL-FILTERED list, which
      // must stay aligned with whatever `selectableContacts` (and therefore the
      // action bar's cost preview) is derived from — resolving from a wider list
      // would reveal, and bill for, a contact the quote never counted.
      const selectedStored = contacts.filter((c) => selectedIds.has(contactKey(c)));
      const selectedDiscovered = discoveredLevelFiltered.filter((c) =>
        selectedIds.has(contactKey(c))
      );
      const selectedContacts = [...selectedStored, ...selectedDiscovered];

      // Skip ON: filter to contacts that still need the field. For 'both', a
      // contact is eligible if EITHER field still needs enrichment. 'found' and
      // 'not_found' are both terminal/free while skip is ON (TD-2302), so a
      // contact resolved on every field is excluded — matching computeRevealCost.
      // Discovered contacts arrive 'not_requested' so they always stay eligible.
      // Skip OFF (TD-2311/TD-2312): forced re-fetch — keep every selected
      // contact (incl. already found/not_found) so the BE re-queries + re-bills
      // them; the request carries skipPreviouslyEnriched=false (see below).
      const isResolved = (status) => status === 'found' || status === 'not_found';
      const eligibleContacts = skipPreviouslyEnriched
        ? selectedContacts.filter((c) =>
            fields.some((f) => !isResolved(c[`enrichment_status_${f}`]))
          )
        : selectedContacts;

      if (eligibleContacts.length === 0) {
        // UX-10: never silently no-op. The action bar already disables
        // 0-credit options, so this only fires on a race (e.g., contact
        // refreshed mid-flight). Tell the user instead of hiding the click.
        store.utilsStore?.setHeaderMessage?.(
          'All selected contacts are already enriched for this field.',
          HEADER_MESSAGE_TYPES.WARNING
        );
        return;
      }
      // TD-2079: stored bulk-reveal matches by contact_enrichment.id (BE
      // bulkReveal), NOT the company_managers/person id. getKeyContacts
      // materialises, so the ce id is present. TD-2357/TD-2421: ephemeral
      // discovered rows have no contact_enrichment_id — send their Surfe identity
      // so the BE mints + reveals them in the SAME job (see partitionRevealTargets).
      const { contactIds, discoveredContacts: discoveredContactsPayload } =
        partitionRevealTargets(eligibleContacts);

      // TD-2263 (Item 5e) + TD-2421: only bail when NEITHER path has anything to
      // send (e.g. stored rows not yet materialised AND no valid discovered
      // identities). Surface a WARNING instead of a silent no-op so the action
      // bar's isRevealing never sticks.
      if (contactIds.length === 0 && discoveredContactsPayload.length === 0) {
        store.utilsStore?.setHeaderMessage?.(
          'These contacts are not ready to enrich yet. Please refresh and try again.',
          HEADER_MESSAGE_TYPES.WARNING
        );
        return;
      }

      // Cost calc uses ELIGIBLE contacts (post-skip filter) so the cost
      // estimate matches what the API will actually bill.
      const cost = computeRevealCost(eligibleContacts, skipPreviouslyEnriched);
      const totalCost = cost[costKey];

      // v3 (2026-05-25): overage UX dropped entirely. Insufficient credits =>
      // toast + abort (BE 402 backstop in `useBulkReveal` is defence-in-depth).
      // Future iteration: replace toast with grey "on hold" state per v3 R4.
      const remaining = subscription?.enrichment?.remaining;
      if (typeof remaining === 'number' && totalCost > remaining) {
        store.utilsStore?.setHeaderMessage?.(
          // A credit-quota hit is a business limit, not a system error — use
          // WARNING (yellow) rather than ERROR (red) so the colour matches
          // the severity and doesn't read as "the app broke".
          'Not enough enrichment credits. Ask your administrator to top up before retrying.',
          HEADER_MESSAGE_TYPES.WARNING
        );
        return;
      }

      // TD-2427: record the submitted targets (keys + field[s]) so each selected
      // card shows the reveal spinner while the job runs. Keyed by contactKey to
      // match ContactCard and `selectedIds`. Uses `eligibleContacts` (post-skip),
      // so unselected cards never animate. `force` = skip OFF (a re-fetch that
      // re-queries already-enriched fields) — the card then animates OVER the
      // exposed value/"Not found" for the submitted field(s); with skip ON an
      // already-resolved field keeps showing its value (it isn't re-queried). Set
      // just before dispatch, cleared on any terminal outcome (effect below).
      setBulkRevealing({
        keys: new Set(eligibleContacts.map(contactKey)),
        fields,
        force: !skipPreviouslyEnriched
      });

      triggerBulkReveal({
        contactIds,
        // TD-2357: ephemeral discovered identities + the CREFO profile id they
        // are minted under. Empty array for a stored-only reveal — the hook then
        // omits the discovered fields, so that request stays byte-identical.
        discoveredContacts: discoveredContactsPayload,
        profileId: companyId,
        fields,
        companyId,
        skipPreviouslyEnriched
      });
    },
    [
      contacts,
      discoveredLevelFiltered,
      selectedIds,
      subscription,
      FIELD_CONFIG,
      triggerBulkReveal,
      store,
      companyId
    ]
  );

  // React to polling outcomes. Completion: clear selection + invite a parent
  // refetch (revealed values land via the next data load). Non-success
  // outcomes toast and reset bulk state — selection persists so the user can
  // retry/adjust. TD-2263 (Item 5): the message is now accurate per outcome —
  // a "found nothing" completion is transparent INFO (not an error), and a
  // poll-ceiling timeout reads differently from a genuine BE failure.
  useEffect(() => {
    // TD-2427: any terminal outcome ends the in-flight bulk overlay so no card is
    // left spinning — on 'completed' the values arrive via the refetch below
    // (render precedence then shows value/"Not found"); on 'timeout'/'failed' the
    // fields fall back to their reveal button. Cleared regardless of what the
    // refetch does, which is what guarantees "no stuck spinners" on every outcome.
    if (jobStatus === 'completed' || jobStatus === 'timeout' || jobStatus === 'failed') {
      setBulkRevealing(null);
    }
    if (jobStatus === 'completed') {
      const enriched = jobResult?.enrichedCount ?? 0;
      if (enriched === 0) {
        // Item 5a: only ELIGIBLE (non-cached) contacts are submitted, so a
        // completed job that enriched nothing means the provider had no
        // email/mobile for them. That's a legitimate, transparent result —
        // INFO, not an error or a "no-op" warning. Copy stays vendor-neutral
        // (no "Surfe") — end users have no context for the enrichment vendor
        // name (TD-2263 Item 3).
        store.utilsStore?.setHeaderMessage?.(
          'No email or mobile was found for the selected contacts.',
          HEADER_MESSAGE_TYPES.INFO
        );
      } else {
        store.utilsStore?.setHeaderMessage?.(
          `${enriched} contact${enriched === 1 ? '' : 's'} enriched.`,
          HEADER_MESSAGE_TYPES.SUCCESS
        );
      }
      setSelectedIds(new Set());
      resetBulkReveal();
      // TD-2351: credits are deducted at job completion (not at the 202-accept),
      // so refetch this tab's subscription instance now — the action-bar counter
      // reads from it, so it updates in place without a page reload. Only ONE
      // refetch: enrichmentAccess hits the same path-global /subscription-status
      // endpoint, and a second concurrent call would race cancelRequest. Issued
      // before onBulkRevealComplete() so the AbortController registers before
      // loadDetails() re-renders (loadDetails hits get-profiler-details, not
      // /subscription-status, so it won't abort this refetch). Only on
      // 'completed' — 'timeout'/'failed' guarantee no deduction.
      subscription.refetch?.();
      if (typeof onBulkRevealComplete === 'function') onBulkRevealComplete();
    } else if (jobStatus === 'timeout') {
      // Item 5b: the poll ceiling was reached while the job was still pending.
      // The job may still finish on the BE, so don't call it "failed" — invite
      // the user to refresh. Selection is preserved either way.
      store.utilsStore?.setHeaderMessage?.(
        'Enrichment is taking longer than expected and is still processing. ' +
          'Refresh in a moment to see results - your selection is preserved.',
        HEADER_MESSAGE_TYPES.WARNING
      );
      resetBulkReveal();
    } else if (jobStatus === 'failed') {
      // UX-4: tell the user their selection survived — they can retry without
      // re-picking the contacts.
      store.utilsStore?.setHeaderMessage?.(
        'Enrichment job failed - your selection is preserved. Please try again.',
        HEADER_MESSAGE_TYPES.ERROR
      );
      resetBulkReveal();
    }
    // store reference is stable from useStore — listing it would only churn deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus, jobResult, onBulkRevealComplete, resetBulkReveal, subscription.refetch]);

  // TD-2455: a SINGLE per-card reveal ("Find email"/"Find mobile") deducts a
  // credit BE-side but — unlike the bulk job above — never refetched this tab's
  // subscription instance, so the "Contact credits N / limit" counter stayed
  // stale until a page reload. Refetch it on each successful reveal (stored AND
  // discovered cards call this via ContactCard's onRevealSuccess). Memoised so it
  // doesn't churn useContactReveal's revealEmail/revealPhone identity per render.
  const handleRevealSuccess = useCallback(() => {
    subscription.refetch?.();
    // subscription.refetch is stable (useSubscriptionStatus memoises it on the
    // stable store); depending on the whole `subscription` object would churn
    // this callback every render — same trade-off the completion effect makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription.refetch]);

  // TD-2481: give the discovery search a visible outcome. `onResults` fires when
  // a search resolves (DiscoverContactsPanel.handleSearch) with the raw mapped
  // contacts — the one place that knows both "a search just finished" and, via
  // the same dedupe the render pipeline uses, how many are actually new. An
  // effect on `discoveredRaw` couldn't tell a real search from the companyId
  // reset above (both can leave it empty), so the branch lives here.
  const handleDiscoverResults = (results) => {
    // Clear any banner still showing from a previous search so a stale "no
    // additional contacts" message can't linger over fresh results (the banner
    // auto-dismisses after ~3s, so two quick searches would otherwise overlap).
    store.utilsStore?.clearHeaderMessage?.();

    // `discoveredDeduped` still holds the PRIOR search's deduped results here
    // (the state update below hasn't rendered yet), so we can distinguish "this
    // search cleared previously shown results" from "nothing was ever found".
    const hadPriorResults = discoveredDeduped.length > 0;
    setDiscoveredRaw(results);

    // Nothing new after dedup vs the stored CREFO contacts (or the search found
    // nothing): DiscoveredContactsSection renders null, so without this the user
    // gets no signal at all. Reuse the header banner — green SUCCESS, as elsewhere.
    if (dedupeDiscoveredContacts(contacts, results).length === 0) {
      store.utilsStore?.setHeaderMessage?.(
        hadPriorResults
          ? 'Discovery executed - no additional contacts found. Previously discovered contacts were cleared.'
          : 'Discovery executed - no additional contacts found.',
        HEADER_MESSAGE_TYPES.SUCCESS
      );
      return;
    }

    // New contacts exist: they render ephemerally below the stored grid, often
    // below the fold. Bring the "Discovered contacts" section into view. Defer a
    // tick so it's mounted (from setDiscoveredRaw) before we measure it, and
    // clear the sticky app header — mirrors scrollToResultsTop and the app's
    // other window-scroll helpers (CompanyDetails scrolls the window, not a
    // container).
    setTimeout(() => {
      const el = document.querySelector('.discovered-contacts-section');
      if (!el) return;
      const headerHeight = document.querySelector('header')?.clientHeight || 0;
      window.scrollTo({
        top: Math.max(el.getBoundingClientRect().top + window.scrollY - headerHeight, 0),
        behavior: 'smooth'
      });
    }, 100);
  };

  // Keep the action bar visible while a selection is held, even if filters
  // hide the underlying cards — otherwise the user can lose track of how many
  // contacts they had picked. The bar still requires enrichment access.
  // TD-2085 (Stream B): when discovery is enabled the bar also hosts the
  // "Discover contacts" trigger (Hamid: discovery + reveal always in the same
  // row), so keep it visible for enrichment users even with zero/again-filtered
  // CREFO contacts — discovery is the whole point when CREFO has no contacts.
  const showActionBar =
    enrichmentAccess.enabled && (hasFilteredContacts || selectedIds.size > 0 || STREAM_B_ENABLED);

  // Memoised so child onLinkedInClick callbacks don't churn on every
  // selectedIds change (would defeat any React.memo on ContactCard).
  const buildLinkedInSearchUrl = useCallback(
    (contact) => {
      const companySearch = encodeURIComponent(
        companyName.toLocaleLowerCase().split(' ').slice(0, 2).join(' ')
      );
      const firstName = encodeURIComponent(contact.first_name);
      const lastName = encodeURIComponent(contact.last_name);
      return `https://www.linkedin.com/search/results/people/?company=${companySearch}&firstName=${firstName}&lastName=${lastName}`;
    },
    [companyName]
  );

  // TD-2505: render the active-filter chips via FiltersChipsList's `chips` API
  // rather than `listConfig`. In listConfig mode the chip ✕ removes a value by
  // mutating the state container in place, which only re-renders when that
  // container is a MobX observable. Here the contact filters are plain React
  // `useState` (self-contained mode, see CompanyDetails), so the in-place splice
  // never triggered a render and the ✕ appeared dead. Routing each chip's
  // onRemove through `handleFilterFieldChange` — the same setter the filter
  // dropdowns above use — updates state correctly in both self-contained and
  // controlled modes. Labels resolve from the same option sources, filtered to
  // the selected values, exactly as the old listConfig reducer did.
  //
  // Shareholder/age labels come straight from the UI_OPTIONS_UTILS constant
  // rather than utilsStore.options: this component is not an observer, so a
  // render-phase read of the store would not be tracked, and utilsStore.options
  // is null until the options request resolves. These two lists are static
  // client-side constants that UtilsStore merely merges into that object, so
  // reading the constant is the same data with no observability or load-order
  // caveat (same pattern as useProfilerViewControls).
  const filterChips = buildFilterChips(
    [
      { field: EMPLOYEES_FILTERS_FIELDS.ROLE, options: roleBucketOptions, prefix: 'role' },
      { field: EMPLOYEES_FILTERS_FIELDS.COUNTRY, options: countryFilterOptions, prefix: 'country' },
      {
        field: EMPLOYEES_FILTERS_FIELDS.IS_SHAREHOLDER,
        options: UI_OPTIONS_UTILS[EMPLOYEES_FILTERS_FIELDS.IS_SHAREHOLDER.VALUES_KEY],
        prefix: 'shareholder'
      },
      {
        field: EMPLOYEES_FILTERS_FIELDS.AGE,
        options: UI_OPTIONS_UTILS[EMPLOYEES_FILTERS_FIELDS.AGE.VALUES_KEY],
        prefix: 'age'
      }
    ],
    filterFields,
    handleFilterFieldChange
  );

  return (
    <div className="key-contacts-wrap">
      {/* Company Details Section */}
      <div className="company-details-wrap">
        <div className="company-details-top">
          <div className={`title ${dataSourceClass(FIELD_SOURCE.companyContact)}`}>
            Company details
          </div>
          {hasContacts && (
            <div className="contacts-wrap">
              {`${contacts.length} ${contacts.length > 1 ? 'contacts' : 'contact'}`}
            </div>
          )}
        </div>
        <div className="company-details-bottom">
          {/* Address — interactive when a Google Maps link exists */}
          <CompanyDetailRow
            icon={ICON_BUTTON_ICONS.GEO_ALT}
            isInteractive={!!companyGoogleMapsLink}
            onActivate={() => window.open(companyGoogleMapsLink, '_blank')}
            displayValue={companyAddress}
          />
          <div className="item-separator" />

          {/* Email — interactive when an email exists (mailto) */}
          <CompanyDetailRow
            icon={ICON_BUTTON_ICONS.ENVELOPE}
            isInteractive={!!companyEmail}
            onActivate={() => window.open(`mailto:${companyEmail}`, '_self')}
            displayValue={companyEmail || PUNCTUATION.EMPTY_VALUE}
          />
          <div className="item-separator" />

          {/* Phone — interactive only on phone/tablet (click-to-call) */}
          <CompanyDetailRow
            icon={ICON_BUTTON_ICONS.TELEPHONE}
            isInteractive={!!(companyPhone && isPhoneOrTablet)}
            onActivate={() => window.open(`tel:${companyPhone}`, '_self')}
            displayValue={companyPhone || PUNCTUATION.EMPTY_VALUE}
          />
        </div>
      </div>

      {/* Contact Details Title */}
      <div className="key-contacts-title">
        <div className={`key-contacts-actual-title ${dataSourceClass(FIELD_SOURCE.contacts)}`}>
          Contact details
        </div>
        {layout === 'mobile' && (
          <IconButton
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.FILTER}
            wrapClassName="mobile-employees-filter-icon"
            tooltipText="Filter contacts"
            tooltipPosition="bottom-end"
            onClick={handleToggleMobileFilterMenu}
            filled={isMobileFilterMenuOpen}
            disabled={!hasContacts}
          />
        )}
      </div>

      {/* Desktop Filters */}
      {layout === 'desktop' && (
        <>
          <div className="desktop-filters-label">Filter contacts by</div>
          <div
            className="desktop-filters-row"
            style={{
              marginBottom: Object.values(filterFields).some((arr) => arr.length) ? '16px' : '24px'
            }}
          >
            <MultiSelect
              field={EMPLOYEES_FILTERS_FIELDS.ROLE}
              value={filterFields[EMPLOYEES_FILTERS_FIELDS.ROLE.NAME] || []}
              setFieldValue={handleFilterFieldChange}
              values={roleBucketOptions}
              hideLabel
              disabled={!hasContacts}
              forceInputBorder={!!(filterFields[EMPLOYEES_FILTERS_FIELDS.ROLE.NAME] || []).length}
            />
            <MultiSelect
              field={EMPLOYEES_FILTERS_FIELDS.COUNTRY}
              value={filterFields[EMPLOYEES_FILTERS_FIELDS.COUNTRY.NAME] || []}
              setFieldValue={handleFilterFieldChange}
              values={countryFilterOptions}
              hideLabel
              disabled={!hasContacts}
              forceInputBorder={
                !!(filterFields[EMPLOYEES_FILTERS_FIELDS.COUNTRY.NAME] || []).length
              }
            />
            <MultiSelect
              field={EMPLOYEES_FILTERS_FIELDS.IS_SHAREHOLDER}
              value={filterFields[EMPLOYEES_FILTERS_FIELDS.IS_SHAREHOLDER.NAME] || []}
              setFieldValue={handleFilterFieldChange}
              hideLabel
              forceSingleSelect
              disabled={!hasContacts}
              forceInputBorder={
                !!(filterFields[EMPLOYEES_FILTERS_FIELDS.IS_SHAREHOLDER.NAME] || []).length
              }
            />
            <MultiSelect
              field={EMPLOYEES_FILTERS_FIELDS.AGE}
              value={filterFields[EMPLOYEES_FILTERS_FIELDS.AGE.NAME] || []}
              setFieldValue={handleFilterFieldChange}
              hideLabel
              disabled={!hasContacts}
              forceInputBorder={!!(filterFields[EMPLOYEES_FILTERS_FIELDS.AGE.NAME] || []).length}
            />
          </div>
          <FiltersChipsList chips={filterChips} />
        </>
      )}

      {/* Mobile Filter Popup */}
      {isMobileFilterMenuOpen && (
        <Portal.Root>
          <div className="cd-mobile-employee-filter-popup-closer" />
          <div className="cd-mobile-employee-filter-popup">
            <div className="header">
              <div className="title">Filter</div>
              <IconButton
                id="cd-close-mobile-employee-filter-popup"
                withFill={false}
                withBorder={false}
                icon={ICON_BUTTON_ICONS.X_LG}
                onClick={handleToggleMobileFilterMenu}
              />
            </div>
            <div className="cd-mobile-employee-filter-wrap">
              <MultiSelectRow
                field={EMPLOYEES_FILTERS_FIELDS.ROLE}
                value={filterFields[EMPLOYEES_FILTERS_FIELDS.ROLE.NAME] || []}
                setFieldValue={handleFilterFieldChange}
                values={roleBucketOptions}
              />
              <div className="cd-mobile-employees-filter-row-separator" />
              <MultiSelectRow
                field={EMPLOYEES_FILTERS_FIELDS.COUNTRY}
                value={filterFields[EMPLOYEES_FILTERS_FIELDS.COUNTRY.NAME] || []}
                setFieldValue={handleFilterFieldChange}
                values={countryFilterOptions}
              />
              <div className="cd-mobile-employees-filter-row-separator" />
              <MultiSelectRow
                field={EMPLOYEES_FILTERS_FIELDS.IS_SHAREHOLDER}
                value={filterFields[EMPLOYEES_FILTERS_FIELDS.IS_SHAREHOLDER.NAME] || []}
                setFieldValue={handleFilterFieldChange}
                forceSingleSelect
                selectedIcon={ICON_BUTTON_ICONS.CHECK_CIRCLE_FILL}
                notSelectedIcon={ICON_BUTTON_ICONS.CIRCLE}
              />
              <div className="cd-mobile-employees-filter-row-separator" />
              <MultiSelectRow
                field={EMPLOYEES_FILTERS_FIELDS.AGE}
                value={filterFields[EMPLOYEES_FILTERS_FIELDS.AGE.NAME] || []}
                setFieldValue={handleFilterFieldChange}
              />
            </div>
            <div className="cd-mobile-employee-filter-actions">
              <IconButton
                type={ICON_BUTTON_TYPES.BLUE}
                innerText="Reset all"
                withFill={false}
                onClick={() => {
                  handleResetFilters();
                  handleToggleMobileFilterMenu();
                }}
              />
              <IconButton
                type={ICON_BUTTON_TYPES.BLUE}
                innerText="Show contacts"
                filled={true}
                onClick={handleToggleMobileFilterMenu}
              />
            </div>
          </div>
        </Portal.Root>
      )}

      {/* No Contacts Messages */}
      {hasContacts && !hasFilteredContacts && (
        <div className="no-key-contacts">There are no contacts based on filter criteria.</div>
      )}
      {!hasContacts && (
        <div className="no-key-contacts">There are no contacts available for this company.</div>
      )}

      {/* Phase 3b: bulk selection action bar (enrichment users only) */}
      {showActionBar && (
        <KeyContactsActionBar
          contacts={selectableContacts}
          // TD-2549: cost preview counts the full billable selection (incl.
          // filter-hidden), so the quoted credits match what a reveal bills.
          allContacts={allSelectableContacts}
          selectedIds={selectedIds}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onReveal={handleBulkReveal}
          isRevealing={isSubmitting || jobStatus === 'pending'}
          // TD-2351: feed the counter from this tab's single subscription
          // instance so the completion-effect refetch below updates it in place.
          enrichment={subscription.enrichment}
          isLoading={subscription.isLoading}
          // TD-2485: on mobile the bar becomes the sticky bottom bar + reveal sheet.
          layout={layout}
          // TD-2085 (Stream B): discovery trigger sits in the same row, left of
          // the reveal controls. companyId is the CREFO profile id the discover
          // endpoint scopes the Surfe search to.
          discoverSlot={
            STREAM_B_ENABLED && companyId ? (
              <DiscoverContactsPanel
                companyId={companyId}
                onResults={handleDiscoverResults}
                onCriteriaChange={setActiveLevels}
                layout={layout}
              />
            ) : null
          }
        />
      )}

      {/* Phase 3c: skipped-contacts summary. Surfaces the BE's per-contact
          skip reasons (erasure, not_found) so the user understands why
          `accepted.length < contactIds.length`. Wrapper renders
          unconditionally so screen readers attach to the live region BEFORE
          the first announcement (conditionally-rendered aria-live nodes are
          missed by VoiceOver/NVDA on first append). */}
      <div className="key-contacts-skipped-summary-wrap" aria-live="polite" aria-atomic="true">
        {bulkSkipped.length > 0 && (
          <details className="key-contacts-skipped-summary">
            <summary>
              {bulkSkipped.length} contact{bulkSkipped.length === 1 ? '' : 's'} could not be
              enriched - click to see reasons
            </summary>
            <ul>
              {bulkSkipped.map((s, idx) => {
                // Resolve skipped rows to display names where we can. Stored
                // skips ('not_found', 'erasure') carry an id we resolve against
                // the stored list; TD-2357 discovered skips ('persist_failed')
                // carry the Surfe identity (first/last/linkedin) instead.
                const match = s.id ? contacts.find((c) => c.id === s.id) : null;
                const displayName =
                  `${s.first_name ?? match?.first_name ?? ''} ${
                    s.last_name ?? match?.last_name ?? ''
                  }`.trim() || 'Unknown contact';
                const reasonLabel = SKIP_REASON_LABELS[s.reason] || s.reason;
                return (
                  <li key={s.id ?? s.linkedin_url ?? `${displayName}-${idx}`}>
                    {displayName}: {reasonLabel}
                  </li>
                );
              })}
            </ul>
          </details>
        )}
      </div>

      {/* Contacts List */}
      {hasFilteredContacts && (
        <div className="key-contacts-container">
          {filteredContacts.map((contact) => (
            <ContactCard
              contact={contact}
              onLinkedInClick={() => window.open(buildLinkedInSearchUrl(contact), '_blank')}
              onViewDetails={onViewDetails}
              onOpenDetailsInNewTab={onOpenDetailsInNewTab}
              selected={selectedIds.has(contactKey(contact))}
              // TD-2263 (Items 2+8): hoist enrichment access. The tab owns the
              // single useEnrichmentAccess() instance (one /subscription-status
              // for the whole list) and hands it to every card, so mounting N
              // cards no longer fires N status calls AND access.enabled is
              // identical across cards — eliminating the per-card race against
              // the path-global cancelRequest that intermittently hid reveal
              // buttons. ContactCard's `showSelectionCheckbox` /
              // `showRevealSection` both read this same hoisted access.
              access={enrichmentAccess}
              onSelectionChange={handleSelectionChange}
              // The reveal endpoint takes the company profile id (BE derives the
              // company name from it) plus the person's crefo id. The reveal
              // targets this company's contacts, so the company in view is the
              // correct context.
              // TD-2124: an indirect owner is revealed against the company they own
              // directly (the BE's anchor), not the target they don't work at.
              companyId={contact.anchor_company_id ?? companyId}
              personCrefoId={contact.person_crefo_id}
              // TD-2455: refresh the credit counter after a single reveal.
              onRevealSuccess={handleRevealSuccess}
              // TD-2427: in-flight bulk reveal overlay — a selected card shows the
              // reveal spinner on the submitted field(s) while the job runs.
              bulkRevealing={bulkRevealing}
              key={contact.id}
            />
          ))}
        </div>
      )}

      {/* TD-2085 (Stream B): ephemeral discovered contacts, rendered below the
          stored list behind a "Discovered contacts" separator. The hoisted
          enrichment access is threaded so each card reuses it (no per-card
          fetch). LinkedIn opens the Surfe-provided URL when present, else a
          name search. Returns null when there are no results. */}
      {STREAM_B_ENABLED && (
        <DiscoveredContactsSection
          contacts={discoveredContacts}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          hiddenByFilters={discoveredHiddenByFilters}
          onClearFilters={handleResetFilters}
          access={enrichmentAccess}
          companyId={companyId}
          // TD-2455: refresh the credit counter after a discovered reveal too.
          onRevealSuccess={handleRevealSuccess}
          // TD-2427: forward the in-flight bulk overlay so discovered cards also
          // show the reveal spinner during a bulk job (matched by contactKey).
          bulkRevealing={bulkRevealing}
          onLinkedInClick={(contact) =>
            window.open(
              // `linkedin_url` is server-supplied — validate it is http(s) before
              // handing it to window.open (a `javascript:`/`data:` URL would run in
              // the opener), and open with noopener/noreferrer. Falls back to the
              // constructed name-search URL when the value is absent or unsafe.
              safeHttpUrl(contact.linkedin_url) || buildLinkedInSearchUrl(contact),
              '_blank',
              'noopener,noreferrer'
            )
          }
        />
      )}

      {/* TD-2485: reserve space so the fixed mobile sticky action bar never covers
          the last cards / discovered results. */}
      {layout === 'mobile' && showActionBar && (
        <div className="key-contacts-sticky-bar-spacer" aria-hidden="true" />
      )}
    </div>
  );
};

// All props are optional: in self-contained mode (`filterFields` omitted) the
// dual-mode props — filteredContacts, filterFields, isMobileFilterMenuOpen,
// onFilterFieldChange, onToggleMobileFilterMenu, onResetFilters, companyId,
// onViewDetails, onOpenDetailsInNewTab, onBulkRevealComplete — are intentionally
// undefined, and the remainder carry default-param fallbacks. Marking any
// `.isRequired` would emit false console warnings in that mode.
KeyContactsTab.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.object),
  filteredContacts: PropTypes.arrayOf(PropTypes.object),
  companyName: PropTypes.string,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyAddress: PropTypes.string,
  companyGoogleMapsLink: PropTypes.string,
  companyEmail: PropTypes.string,
  companyPhone: PropTypes.string,
  layout: PropTypes.string,
  isPhoneOrTablet: PropTypes.bool,
  filterFields: PropTypes.object,
  roleFilterOptions: PropTypes.array,
  countryFilterOptions: PropTypes.array,
  isMobileFilterMenuOpen: PropTypes.bool,
  onFilterFieldChange: PropTypes.func,
  onToggleMobileFilterMenu: PropTypes.func,
  onResetFilters: PropTypes.func,
  onViewDetails: PropTypes.func,
  onOpenDetailsInNewTab: PropTypes.func,
  onBulkRevealComplete: PropTypes.func
};

export default KeyContactsTab;
